import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Camera, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_PHOTOS = [];
export default function PhotoCapture({ onPhotosChange, maxPhotos = 3, existingPhotos = EMPTY_PHOTOS, testIdPrefix = '' }) {
  const [photos, setPhotos] = useState(existingPhotos);
  const [capturing, setCapturing] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  useEffect(() => { setPhotos(existingPhotos); }, [existingPhotos]);
  const testId = (key) => testIdPrefix ? `${testIdPrefix}-${key}` : key;

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    setCapturing(true);
    const newPhotos = [];

    for (const file of files) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error('Choose a JPEG, PNG or WebP photo.'); continue;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(`${file.name} is too large. Max 5MB per photo.`);
        continue;
      }

      try {
        const base64 = await fileToBase64(file);
        newPhotos.push({
          data: base64,
          name: file.name,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error processing photo:', error);
      }
    }

    const updatedPhotos = [...photos, ...newPhotos];
    setPhotos(updatedPhotos);
    onPhotosChange(updatedPhotos);
    setCapturing(false);
    toast.success(`${newPhotos.length} photo(s) added`);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    const updatedPhotos = photos.filter((_, i) => i !== index);
    setPhotos(updatedPhotos);
    onPhotosChange(updatedPhotos);
    toast.success('Photo removed');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => cameraInputRef.current?.click()}
          disabled={photos.length >= maxPhotos || capturing}
          className="flex-1 text-black bg-white hover:bg-zinc-100 hover:text-black"
          data-testid={testId('camera-button')}
        >
          <Camera className="h-4 w-4 mr-2" />
          Take Photo
        </Button>
        
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={photos.length >= maxPhotos || capturing}
          className="flex-1 text-black bg-white hover:bg-zinc-100 hover:text-black"
          data-testid={testId('upload-button')}
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload Photo
        </Button>
      </div>

      <input
        ref={cameraInputRef}
        data-testid={testId('camera-input')}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        multiple={maxPhotos > 1}
      />

      <input
        ref={fileInputRef}
        data-testid={testId('upload-input')}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        multiple={maxPhotos > 1}
      />

      {photos.length > 0 && (
        <div className={maxPhotos === 1 ? 'grid grid-cols-1 max-w-lg gap-3' : 'grid grid-cols-3 gap-3'}>
          {photos.map((photo, index) => (
            <Card key={index} className="relative p-2">
              <img
                src={photo.data}
                alt={`Photo ${index + 1}`}
                className="w-full aspect-[4/3] object-contain rounded"
              />
              <Button
                type="button"
                aria-label={`Remove photo ${index + 1}`}
                title={`Remove photo ${index + 1}`}
                size="sm"
                variant="destructive"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                onClick={() => removePhoto(index)}
                data-testid={testId(`remove-photo-${index}`)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-zinc-500">
        {photos.length} / {maxPhotos} photos • Max 5MB per photo
      </p>
    </div>
  );
}
